import {
  AlertCircle,
  Download,
  Images,
  Loader2,
  Sparkles,
  Wand2,
  XCircle
} from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { CropEditor } from "./components/CropEditor";
import { HistoryPanel } from "./components/HistoryPanel";
import { ImageLightbox, type LightboxImage } from "./components/ImageLightbox";
import { ImageDropzone } from "./components/ImageDropzone";
import { APP_NAME, DEFAULT_OPTIONS } from "./lib/constants";
import { ApiError, requestGeneratedNotes, requestTryOn } from "./lib/api";
import { prepareImage, resolveOutputSize, sizeToAspect } from "./lib/image";
import { clearJobs, deleteJob, listJobs, saveJob } from "./lib/storage";
import type { CropArea, PreparedImage, TryOnJob, TryOnOptions } from "./lib/types";

type ImageKind = "hand" | "nail";

interface ImageState {
  file?: File;
  prepared?: PreparedImage;
  cropArea?: CropArea;
  rotation: number;
  busy: boolean;
  error?: string;
}

const PREFERENCES_KEY = "meijia.tryon.preferences";
const GENERATION_MESSAGES = [
  "正在识别甲面轮廓和手部光线",
  "正在融合参考款式的颜色、纹理和光泽",
  "正在校准指尖边缘，让贴合更自然",
  "正在打磨最后一层真实感"
];
const SLOGANS = ["灵感先试戴，喜欢再上手", "把想象中的款式，落到你的指尖"];
const RISK_TIPS = [
  "AI 试戴结果仅供造型参考，实际颜色、长度和贴合度可能存在偏差。",
  "请上传本人或已获授权的图片，避免包含身份证件、住址、聊天记录等敏感信息。",
  "生成结果可能受光线、遮挡、角度影响；下单或到店前建议再次确认款式细节。"
];

export function App() {
  const [hand, setHand] = useState<ImageState>({ rotation: 0, busy: false });
  const [nail, setNail] = useState<ImageState>({ rotation: 0, busy: false });
  const [options, setOptions] = useState<TryOnOptions>(() => loadPreferences());
  const [jobs, setJobs] = useState<TryOnJob[]>([]);
  const [activeJob, setActiveJob] = useState<TryOnJob | null>(null);
  const [isGenerating, setIsGenerating] = useState(false);
  const [isSuggestingNotes, setIsSuggestingNotes] = useState(false);
  const [generationStep, setGenerationStep] = useState(0);
  const [globalError, setGlobalError] = useState<string | null>(null);
  const [previewImage, setPreviewImage] = useState<LightboxImage | null>(null);
  const [cropEditor, setCropEditor] = useState<{
    kind: ImageKind;
    title: string;
    image: PreparedImage;
    initialCrop?: CropArea;
  } | null>(null);

  useEffect(() => {
    listJobs().then(setJobs).catch(() => setJobs([]));
  }, []);

  useEffect(() => {
    localStorage.setItem(PREFERENCES_KEY, JSON.stringify(options));
  }, [options]);

  useEffect(() => {
    const hasTemporaryImages = Boolean(hand.file || nail.file || isGenerating);
    if (!hasTemporaryImages) return;

    const handleBeforeUnload = (event: BeforeUnloadEvent) => {
      event.preventDefault();
      event.returnValue = "";
    };

    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => window.removeEventListener("beforeunload", handleBeforeUnload);
  }, [hand.file, nail.file, isGenerating]);

  useEffect(() => {
    if (!isGenerating) {
      setGenerationStep(0);
      return;
    }

    const timer = window.setInterval(() => {
      setGenerationStep((step) => step + 1);
    }, 2400);
    return () => window.clearInterval(timer);
  }, [isGenerating]);

  const resolvedSize = useMemo(() => {
    const image = hand.prepared;
    if (!image) return options.size === "auto" ? "1024x1024" : options.size;
    return resolveOutputSize(options.size, image.width, image.height);
  }, [hand.prepared, options.size]);

  const canGenerate = Boolean(hand.prepared && nail.prepared && !hand.busy && !nail.busy && !isGenerating);

  async function handleSelect(kind: ImageKind, file: File) {
    await processImage(kind, file, 0, options);
  }

  async function handleRotate(kind: ImageKind, direction: -1 | 1) {
    const current = kind === "hand" ? hand : nail;
    if (!current.file) return;
    const nextRotation = current.rotation + direction * 90;
    await processImage(kind, current.file, nextRotation, options, current.cropArea);
  }

  async function processImage(
    kind: ImageKind,
    file: File,
    rotation: number,
    nextOptions: TryOnOptions,
    cropArea?: CropArea
  ) {
    const setState = kind === "hand" ? setHand : setNail;
    setState((state) => ({ ...state, file, cropArea, rotation, busy: true, error: undefined }));
    setGlobalError(null);

    try {
      const explicitSize = nextOptions.size === "auto" ? undefined : nextOptions.size;
      const prepared = await prepareImage(file, {
        rotation,
        cropArea,
        cropMode: kind === "hand" ? nextOptions.cropMode : "contain",
        targetAspect: explicitSize ? sizeToAspect(explicitSize) : undefined
      });
      setState({ file, prepared, cropArea, rotation: prepared.rotation, busy: false });
    } catch (error) {
      setState((state) => ({
        ...state,
        file,
        cropArea,
        rotation,
        busy: false,
        error: error instanceof Error ? error.message : "图片处理失败。"
      }));
    }
  }

  async function updateOptions(next: Partial<TryOnOptions>) {
    const merged = { ...options, ...next };
    setOptions(merged);
    if (hand.file && (next.cropMode || next.size)) {
      await processImage("hand", hand.file, hand.rotation, merged, hand.cropArea);
    }
  }

  function clearImage(kind: ImageKind) {
    const title = kind === "hand" ? "手部照片" : "美甲参考";
    if (!confirmAction(`确定要删除${title}吗？删除后需要重新选择图片。`)) return;
    if (kind === "hand") setHand({ rotation: 0, busy: false });
    else setNail({ rotation: 0, busy: false });
  }

  function previewPreparedImage(title: string, image?: PreparedImage) {
    if (!image) return;
    setPreviewImage({
      src: image.dataUri,
      title,
      meta: imageMeta(image)
    });
  }

  function previewJob(job: TryOnJob) {
    if (!job.resultImage) return;
    setPreviewImage({
      src: job.resultImage,
      title: "试戴结果",
      meta: new Intl.DateTimeFormat("zh-CN", {
        month: "2-digit",
        day: "2-digit",
        hour: "2-digit",
        minute: "2-digit"
      }).format(new Date(job.createdAt))
    });
  }

  function openCrop(kind: ImageKind) {
    const current = kind === "hand" ? hand : nail;
    if (!current.prepared) return;
    setCropEditor({
      kind,
      title: kind === "hand" ? "手部照片" : "美甲参考",
      image: current.prepared,
      initialCrop: current.cropArea
    });
  }

  async function applyCrop(cropArea: CropArea) {
    const editor = cropEditor;
    if (!editor) return;
    const current = editor.kind === "hand" ? hand : nail;
    if (!current.file) return;
    setCropEditor(null);
    await processImage(editor.kind, current.file, current.rotation, options, cropArea);
  }

  async function generateNotes() {
    setIsSuggestingNotes(true);
    setGlobalError(null);
    try {
      const result = await requestGeneratedNotes({
        notes: options.notes,
        styleStrength: options.styleStrength
      });
      await updateOptions({ notes: result.notes });
    } catch (error) {
      setGlobalError(formatError(error));
    } finally {
      setIsSuggestingNotes(false);
    }
  }

  async function generate() {
    if (!hand.prepared || !nail.prepared) return;
    setIsGenerating(true);
    setGlobalError(null);

    const createdAt = new Date().toISOString();
    const localId = crypto.randomUUID();
    const jobOptions: TryOnOptions = { ...options, size: resolvedSize };
    const pendingJob: TryOnJob = {
      id: localId,
      createdAt,
      status: "pending",
      handPreview: hand.prepared.dataUri,
      nailPreview: nail.prepared.dataUri,
      options: jobOptions
    };

    await saveAndRefresh(pendingJob);

    try {
      const result = await requestTryOn({
        handImageDataUri: hand.prepared.dataUri,
        nailImageDataUri: nail.prepared.dataUri,
        options: {
          size: resolvedSize,
          styleStrength: options.styleStrength,
          notes: options.notes
        }
      });
      const completedJob: TryOnJob = {
        ...pendingJob,
        id: result.id,
        createdAt: result.createdAt,
        status: "completed",
        resultImage: result.imageDataUri,
        promptSummary: result.promptSummary
      };
      await deleteJob(localId);
      await saveAndRefresh(completedJob);
      setActiveJob(completedJob);
    } catch (error) {
      const message = formatError(error);
      const failedJob: TryOnJob = {
        ...pendingJob,
        status: "failed",
        error: message
      };
      await saveAndRefresh(failedJob);
      setActiveJob(failedJob);
      setGlobalError(message);
    } finally {
      setIsGenerating(false);
    }
  }

  async function saveAndRefresh(job: TryOnJob) {
    await saveJob(job);
    setJobs(await listJobs());
  }

  async function removeJob(id: string) {
    if (!confirmAction("确定要删除这张历史结果图片吗？删除后无法恢复。")) return;
    await deleteJob(id);
    const nextJobs = await listJobs();
    setJobs(nextJobs);
    if (activeJob?.id === id) setActiveJob(nextJobs[0] ?? null);
  }

  async function clearHistory() {
    if (!confirmAction("确定要清空全部本地历史图片吗？删除后无法恢复。")) return;
    await clearJobs();
    setJobs([]);
    setActiveJob(null);
  }

  function downloadJob(job: TryOnJob) {
    if (!job.resultImage) return;
    const link = document.createElement("a");
    link.href = job.resultImage;
    link.download = `meijia-${job.id}.png`;
    link.click();
  }

  return (
    <main className="app-shell">
      <header className="app-header">
        <div>
          <div className="brand-mark">
            <Sparkles size={22} />
            <span>{APP_NAME}</span>
          </div>
          <p className="tagline">{SLOGANS[0]}</p>
          <p>{SLOGANS[1]}。图片仅保存在本机；生成时会发送到图片生成服务。</p>
        </div>
      </header>

      <section className="workspace">
        <div className="left-rail">
          <div className="upload-grid">
            <ImageDropzone
              title="手部照片"
              image={hand.prepared}
              error={hand.error}
              busy={hand.busy}
              onSelect={(file) => handleSelect("hand", file)}
              onPreview={() => previewPreparedImage("手部照片", hand.prepared)}
              onCrop={() => openCrop("hand")}
              onRotate={(direction) => handleRotate("hand", direction)}
              onClear={() => clearImage("hand")}
            />
            <ImageDropzone
              title="美甲参考"
              image={nail.prepared}
              error={nail.error}
              busy={nail.busy}
              onSelect={(file) => handleSelect("nail", file)}
              onPreview={() => previewPreparedImage("美甲参考", nail.prepared)}
              onCrop={() => openCrop("nail")}
              onRotate={(direction) => handleRotate("nail", direction)}
              onClear={() => clearImage("nail")}
            />
          </div>

          <section className="controls-panel">
            <div className="control-group">
              <label>输出比例</label>
              <div className="segmented">
                {[
                  ["auto", "自动"],
                  ["1024x1024", "方图"],
                  ["1024x768", "横图"],
                  ["768x1024", "竖图"]
                ].map(([value, label]) => (
                  <button
                    key={value}
                    type="button"
                    className={options.size === value ? "active" : ""}
                    onClick={() => updateOptions({ size: value as TryOnOptions["size"] })}
                  >
                    {label}
                  </button>
                ))}
              </div>
              <span className="control-hint">当前 {resolvedSize}</span>
            </div>

            <div className="control-group">
              <label>贴合强度</label>
              <div className="segmented">
                {[
                  ["subtle", "轻柔"],
                  ["balanced", "自然"],
                  ["bold", "鲜明"]
                ].map(([value, label]) => (
                  <button
                    key={value}
                    type="button"
                    className={options.styleStrength === value ? "active" : ""}
                    onClick={() => updateOptions({ styleStrength: value as TryOnOptions["styleStrength"] })}
                  >
                    {label}
                  </button>
                ))}
              </div>
            </div>

            <div className="control-row">
              <label className="toggle-row">
                <input
                  type="checkbox"
                  checked={options.cropMode === "cover"}
                  onChange={(event) =>
                    updateOptions({ cropMode: event.currentTarget.checked ? "cover" : "contain" })
                  }
                />
                <span>适配裁剪</span>
              </label>
            </div>

            <div className="control-group">
              <div className="control-label-row">
                <label htmlFor="notes">补充要求</label>
                <button
                  className="text-button"
                  type="button"
                  onClick={generateNotes}
                  disabled={isSuggestingNotes || isGenerating}
                >
                  {isSuggestingNotes ? <Loader2 className="spin" size={16} /> : <Sparkles size={16} />}
                  AI 生成
                </button>
              </div>
              <textarea
                id="notes"
                value={options.notes}
                rows={3}
                maxLength={300}
                onChange={(event) => updateOptions({ notes: event.target.value })}
                placeholder="例如：保留法式边、不要改变戒指"
              />
            </div>

            <button className="primary-action" type="button" onClick={generate} disabled={!canGenerate}>
              {isGenerating ? <Loader2 className="spin" size={20} /> : <Wand2 size={20} />}
              {isGenerating ? "生成中" : "生成试戴"}
            </button>

            {globalError ? (
              <div className="notice error" role="alert">
                <AlertCircle size={18} />
                <span>{globalError}</span>
              </div>
            ) : (
              <div className="notice">
                <AlertCircle size={18} />
                <span>请确认图片已获得使用授权。</span>
              </div>
            )}

            <section className="risk-panel" aria-labelledby="risk-title">
              <h2 id="risk-title">风险提示</h2>
              <ul>
                {RISK_TIPS.map((tip) => (
                  <li key={tip}>{tip}</li>
                ))}
              </ul>
            </section>
          </section>
        </div>

        <section className="result-panel">
          <div className="panel-heading">
            <h2>试戴结果</h2>
            <button
              className="text-button"
              type="button"
              disabled={!activeJob?.resultImage}
              onClick={() => activeJob && downloadJob(activeJob)}
            >
              <Download size={16} />
              下载
            </button>
          </div>

          <div className="result-stage">
            {isGenerating ? (
              <GenerationStatus message={GENERATION_MESSAGES[generationStep % GENERATION_MESSAGES.length]} />
            ) : activeJob?.resultImage ? (
              <button
                className="result-image-button"
                type="button"
                onClick={() => previewJob(activeJob)}
                aria-label="放大查看美甲试戴结果"
              >
                <img src={activeJob.resultImage} alt="美甲试戴结果" />
              </button>
            ) : activeJob?.status === "failed" ? (
              <div className="result-placeholder failed">
                <XCircle size={28} />
                <span>{activeJob.error}</span>
              </div>
            ) : (
              <div className="result-placeholder">
                <Images size={30} />
                <span>等待生成</span>
              </div>
            )}
          </div>
        </section>
      </section>

      <HistoryPanel
        jobs={jobs}
        onSelect={setActiveJob}
        onDelete={removeJob}
        onClear={clearHistory}
        onDownload={downloadJob}
        onPreview={previewJob}
      />

      <footer className="app-footer">作者：5pyx55CG5ri4</footer>

      {previewImage ? <ImageLightbox image={previewImage} onClose={() => setPreviewImage(null)} /> : null}
      {cropEditor ? (
        <CropEditor
          title={cropEditor.title}
          image={cropEditor.image}
          initialCrop={cropEditor.initialCrop}
          onCancel={() => setCropEditor(null)}
          onApply={applyCrop}
        />
      ) : null}
    </main>
  );
}

function GenerationStatus({ message }: { message: string }) {
  return (
    <div className="generation-status" role="status" aria-live="polite">
      <div className="generation-visual" aria-hidden="true">
        <span className="generation-ring ring-one" />
        <span className="generation-ring ring-two" />
        <span className="generation-scan" />
        <Sparkles size={28} />
      </div>
      <span className="generation-title">AI 正在试戴</span>
      <span className="generation-copy">{message}</span>
    </div>
  );
}

function imageMeta(image: PreparedImage) {
  return `${image.width} x ${image.height} · ${(image.bytes / 1024 / 1024).toFixed(2)} MB`;
}

function loadPreferences(): TryOnOptions {
  try {
    const raw = localStorage.getItem(PREFERENCES_KEY);
    if (!raw) return DEFAULT_OPTIONS;
    return { ...DEFAULT_OPTIONS, ...JSON.parse(raw) };
  } catch {
    return DEFAULT_OPTIONS;
  }
}

function formatError(error: unknown) {
  if (error instanceof ApiError) return error.message;
  if (error instanceof Error) return error.message;
  return "生成失败，请稍后重试。";
}

function confirmAction(message: string) {
  return window.confirm(message);
}
